import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusLabels = {
  'pending': { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' },
  'approved': { label: 'Approved', color: 'bg-green-100 text-green-800' },
  'rejected': { label: 'Rejected', color: 'bg-red-100 text-red-800' }
};

export default function CheckBooking() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [bookings, setBookings] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    try {
      const response = await axios.post(`${API}/bookings/check`, { email });
      setBookings(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info('No bookings found with that email');
      }
    } catch (error) {
      toast.error('Error searching bookings');
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-home-btn">
            <ArrowLeft className="mr-2" /> Back to Home
          </Button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold elegant-script mb-8" data-testid="check-booking-title">Check My Booking</h1>

        <Card className="mb-8">
          <CardContent className="p-6">
            <Label htmlFor="search-email" className="mb-2 block">Enter your email address</Label>
            <div className="flex gap-2">
              <Input 
                id="search-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="search-email-input"
              />
              <Button onClick={handleSearch} className="bg-black text-[#e8dcc8]" data-testid="search-bookings-btn">
                <Search className="mr-2" /> Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {searched && bookings.length > 0 && (
          <div className="space-y-4" data-testid="bookings-results">
            <h2 className="text-xl font-bold mb-4">Your Bookings</h2>
            {bookings.map((booking) => (
              <Card key={booking.id} data-testid={`booking-result-${booking.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg">{booking.serviceName}</h3>
                      <p className="text-gray-600">with {booking.professionalName}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusLabels[booking.status]?.color || 'bg-gray-100'}`} data-testid={`booking-status-${booking.id}`}>
                      {statusLabels[booking.status]?.label || booking.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-600">Date</Label>
                      <p className="font-semibold">{new Date(booking.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <div>
                      <Label className="text-gray-600">Time</Label>
                      <p className="font-semibold">{booking.time}</p>
                    </div>
                  </div>
                  {booking.status === 'rejected' && (
                    <p className="mt-4 text-sm text-red-600">
                      This booking was rejected. Please try booking at a different time.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
